/**
 * Month-grid arithmetic for the community calendar.
 *
 * Pure functions over dates, kept apart from the components so the awkward parts —
 * month boundaries, leap years, the Monday-first week, and the gap between an event's
 * UTC instant and the member's local calendar day — are unit-testable without a DOM.
 *
 * Rolling this rather than adding a date library is deliberate: it is about eighty
 * lines, and a calendar is the one thing in the app where the edge cases are known and
 * finite. `Intl` already does the hard part (formatting and timezones).
 */

/** A single cell in the month grid. */
export interface CalendarDay {
  /** Local-midnight Date for the cell. */
  date: Date;
  /** `YYYY-MM-DD` in local time — the key events are grouped under. */
  key: string;
  /** False for the leading/trailing days that pad the grid to whole weeks. */
  inMonth: boolean;
  isToday: boolean;
}

/**
 * A local calendar-day key.
 *
 * Deliberately *not* `toISOString().slice(0, 10)`, which would convert to UTC first: an
 * event at 23:00 in Prague would land on the following day's cell.
 */
export function dayKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Local midnight on the given day. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Local midnight on the first of the month. */
export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Move by whole months, clamping the day.
 *
 * `new Date(2026, 0, 31)` plus one month would otherwise roll into March, because
 * setting the month keeps the day-of-month. Building from the first avoids that.
 */
export function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

/** Whether two dates fall on the same local day. */
export function isSameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

/**
 * The half-open window a month view needs from the API.
 *
 * It spans the whole grid, not just the month, so events on the padding days from the
 * neighbouring months are fetched too — the cells are on screen, so their dots must be
 * as well. The bounds are local midnights, serialised as UTC instants for the query.
 */
export function monthWindow(
  month: Date,
  weekStartsOn: number = 1,
): { from: Date; to: Date } {
  const grid = buildMonthGrid(month, weekStartsOn);
  const first = grid[0][0].date;
  const lastWeek = grid[grid.length - 1];
  const last = lastWeek[lastWeek.length - 1].date;
  // Exclusive upper bound: local midnight *after* the final cell.
  return {
    from: first,
    to: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
  };
}

/**
 * Build the weeks of a month view.
 *
 * @param month - Any date inside the month to render.
 * @param weekStartsOn - 0 = Sunday, 1 = Monday. Defaults to Monday, which is the
 *   convention in both markets the app ships into.
 * @param today - Injectable so tests need not mock the clock.
 */
export function buildMonthGrid(
  month: Date,
  weekStartsOn: number = 1,
  today: Date = new Date(),
): CalendarDay[][] {
  const first = startOfMonth(month);
  // How many days of the previous month pad the first row.
  const lead = (first.getDay() - weekStartsOn + 7) % 7;

  const start = new Date(first.getFullYear(), first.getMonth(), 1 - lead);
  const monthIndex = first.getMonth();

  const weeks: CalendarDay[][] = [];
  const cursor = new Date(start);

  // Whole weeks until the month is covered — five or six rows depending on the month.
  do {
    const week: CalendarDay[] = [];
    for (let index = 0; index < 7; index += 1) {
      const date = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        cursor.getDate(),
      );
      week.push({
        date,
        key: dayKey(date),
        inMonth: date.getMonth() === monthIndex,
        isToday: isSameDay(date, today),
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  } while (cursor.getMonth() === monthIndex);

  return weeks;
}

/**
 * Group items under the local day they start on.
 *
 * Takes an accessor rather than a fixed field so it stays independent of the event
 * wire type.
 */
export function groupByDay<T>(
  items: T[],
  startsAt: (item: T) => string,
): Map<string, T[]> {
  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const key = dayKey(new Date(startsAt(item)));
    const bucket = grouped.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      grouped.set(key, [item]);
    }
  }
  return grouped;
}

/** Weekday labels for the grid header, in the given locale, starting on `weekStartsOn`. */
export function weekdayLabels(
  locale: string,
  weekStartsOn: number = 1,
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
  // 2024-01-01 was a Monday, which makes the offset arithmetic obvious.
  const monday = new Date(2024, 0, 1);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + ((index + weekStartsOn - 1 + 7) % 7));
    return formatter.format(date);
  });
}
